import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createTrip } from '../lib/api';
import { addAccessedCode } from '../lib/tripHistory';
import { setTripAccess } from '../lib/tripAccess';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Golf, UsersThree, Trophy } from '@phosphor-icons/react';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';

const EVENT_TYPES = [
  { value: 'trip', label: 'Golf Trip', icon: Golf, desc: 'Multi-round trip' },
  { value: 'society', label: 'Society Day', icon: UsersThree, desc: 'Single-day event' },
  { value: 'casual', label: 'Casual Round', icon: Trophy, desc: 'Informal round' },
];

export default function CreateEvent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ownerToken, setOwnerToken] = useState(
    location.state?.ownerToken || sessionStorage.getItem('foretour_platform_token') || null
  );
  const [eventType, setEventType] = useState('trip');
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [maxRounds, setMaxRounds] = useState(1);
  const [maxPlayers, setMaxPlayers] = useState(16);
  const [maxRoundsRaw, setMaxRoundsRaw] = useState('1');
  const [maxPlayersRaw, setMaxPlayersRaw] = useState('16');
  const [teamScoring, setTeamScoring] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdTrip, setCreatedTrip] = useState(null);
  const [showInlineOwnerDialog, setShowInlineOwnerDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const doCreate = async (token) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await createTrip({
        name: name.trim(),
        pin: pin.trim() || null,
        user_password: userPassword.trim(),
        max_rounds: maxRounds,
        max_players: maxPlayers,
        event_type: eventType,
        team_scoring: teamScoring,
      }, token);
      sessionStorage.removeItem('foretour_platform_token');
      const tripCode = res.data.code;
      const returnedPin = res.data.pin;
      addAccessedCode(tripCode);
      setTripAccess(tripCode, 'organiser', returnedPin, false);
      setCreatedTrip({
        code: tripCode,
        emailStatus: res.data.owner_password_email_status || 'failed',
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === 'owner_required') {
        // Token was stale/evicted — clear it and prompt for re-entry
        setOwnerToken(null);
        sessionStorage.removeItem('foretour_platform_token');
        setPendingSubmit(true);
        setShowInlineOwnerDialog(true);
      } else {
        setError(detail || 'Failed to create');
      }
    } finally { setSubmitting(false); }
  };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (userPassword.trim().length < 4) { setError('Player code must be at least 4 characters'); return; }
    if (pin.trim() && pin.trim() === userPassword.trim()) { setError('Organiser code and Player code must be different'); return; }
    if (ownerToken) {
      await doCreate(ownerToken);
    } else {
      setPendingSubmit(true);
      setShowInlineOwnerDialog(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="create-event">
      {/* Master password reveal dialog */}
      <Dialog open={!!createdTrip} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-sm rounded-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1D242B]">Event Created</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {createdTrip?.emailStatus === 'sent' ? (
              <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl p-4 space-y-2" data-testid="email-sent-panel">
                <p className="text-sm font-bold text-[#2E7D32]">Master password sent</p>
                <p className="text-sm text-[#2E7D32]">Master password sent to <strong>acottrill@foretour.co.uk</strong>. Check your inbox — only needed to change scores or rounds after scoring has started.</p>
              </div>
            ) : (
              <div className="bg-[#FFF3E0] border border-[#FFE0B2] rounded-xl p-4 space-y-2" data-testid="email-failed-panel">
                <p className="text-sm font-bold text-[#E59000]">Email delivery failed</p>
                <p className="text-sm text-[#E59000]">Master password could not be emailed — contact support. The event has been created, but actions on scored rounds will need the admin regeneration path until this is resolved.</p>
              </div>
            )}
            <Button onClick={() => navigate(`/trip/${createdTrip?.code}`)}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-bold"
              data-testid="owner-password-continue-btn">
              Continue to Event
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="p-4 flex items-center gap-3 max-w-lg mx-auto">
        <button onClick={() => navigate('/home')} className="w-10 h-10 rounded-full bg-white border border-[#E1E6E4] flex items-center justify-center" data-testid="create-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#1D242B]" weight="bold" />
        </button>
        <h1 className="text-xl font-bold text-[#1D242B]">New Event</h1>
      </div>
      <div className="px-4 max-w-lg mx-auto space-y-5 pb-8">
        {/* Event type */}
        <div className="grid grid-cols-3 gap-3">
          {EVENT_TYPES.map(({ value, label, icon: Icon, desc }) => (
            <button key={value} onClick={() => setEventType(value)}
              className={`p-3 rounded-xl border text-center transition-all ${eventType === value ? 'border-[#1B4332] bg-[#1B4332]/5' : 'border-[#E1E6E4] bg-white'}`}
              data-testid={`event-type-${value}`}>
              <Icon className={`w-6 h-6 mx-auto mb-1 ${eventType === value ? 'text-[#1B4332]' : 'text-[#B8C7C0]'}`} weight="fill" />
              <p className="text-xs font-semibold text-[#1D242B]">{label}</p>
              <p className="text-[10px] text-[#455561]">{desc}</p>
            </button>
          ))}
        </div>
        {/* Name */}
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Event Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spain 2026"
            className="h-14 rounded-xl text-base" data-testid="event-name-input" />
        </div>
        {/* Organiser code */}
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Organiser code</label>
          <Input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Leave blank to auto-generate"
            className="h-14 rounded-xl text-base" data-testid="event-pin-input" />
          <p className="text-xs text-[#8A9BA8] mt-1">Your organiser login. Use it to manage your event. Keep it to yourself.</p>
        </div>
        {/* Player code */}
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Player code</label>
          <Input value={userPassword} onChange={(e) => setUserPassword(e.target.value)} placeholder="Min 4 characters"
            className="h-14 rounded-xl text-base" data-testid="event-user-password-input" />
          <p className="text-xs text-[#8A9BA8] mt-1">Share with your players. They use it to join the event and enter scores.</p>
        </div>
        {/* Caps */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Max Rounds</label>
            <Input type="number" min={1} value={maxRoundsRaw}
              onChange={(e) => {
                setMaxRoundsRaw(e.target.value);
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n) && n >= 1) setMaxRounds(n);
              }}
              onBlur={() => {
                const n = parseInt(maxRoundsRaw, 10);
                const clamped = !Number.isFinite(n) || n < 1 ? 1 : n;
                setMaxRounds(clamped);
                setMaxRoundsRaw(String(clamped));
              }}
              className="h-14 rounded-xl text-base" data-testid="max-rounds-input" />
            <p className="text-xs text-[#8A9BA8] mt-1">Total rounds you can create (deleting doesn't refund).</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Max Players</label>
            <Input type="number" min={1} value={maxPlayersRaw}
              onChange={(e) => {
                setMaxPlayersRaw(e.target.value);
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n) && n >= 1) setMaxPlayers(n);
              }}
              onBlur={() => {
                const n = parseInt(maxPlayersRaw, 10);
                const clamped = !Number.isFinite(n) || n < 1 ? 1 : n;
                setMaxPlayers(clamped);
                setMaxPlayersRaw(String(clamped));
              }}
              className="h-14 rounded-xl text-base" data-testid="max-players-input" />
            <p className="text-xs text-[#8A9BA8] mt-1">Total players you can add (removing doesn't refund).</p>
          </div>
        </div>
        {/* Team scoring */}
        <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-[#E1E6E4]">
          <span className="text-sm font-semibold text-[#1D242B]">Team Scoring</span>
          <Switch checked={teamScoring} onCheckedChange={setTeamScoring} data-testid="team-scoring-toggle" />
        </div>

        {error && <p className="text-sm text-[#D32F2F] text-center" data-testid="create-error">{error}</p>}

        <Button onClick={handleCreate} disabled={submitting || !name.trim() || userPassword.trim().length < 4}
          className="w-full h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-base font-bold" data-testid="create-submit-btn">
          {submitting ? 'Creating...' : 'Create Event'}
        </Button>
      </div>
      {/* Inline owner password dialog — fallback when token lost (iOS Safari state eviction) */}
      <OwnerPasswordDialog
        open={showInlineOwnerDialog}
        onOpenChange={(open) => { if (!open) { setShowInlineOwnerDialog(false); setPendingSubmit(false); } }}
        onSuccess={(token) => {
          setOwnerToken(token);
          sessionStorage.setItem('foretour_platform_token', token);
          setShowInlineOwnerDialog(false);
          if (pendingSubmit) doCreate(token);
        }}
        description="Your session expired. Re-enter the owner password to create this event."
      />
    </div>
  );
}
